import React, { useMemo, useState } from 'react';
import { ArrowLeft } from './Icons';
import BottomNavigation from './BottomNavigation';
import { communityLearningData } from '../data/communityLearningData';
import { useSupabaseData } from '../hooks/useSupabaseData';

const CommunityLearningPage = ({ onBack, userPreferences }) => {
  const [posts, setPosts] = useState(communityLearningData.communityFeed.posts);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [commentText, setCommentText] = useState('');
  
  const { displayLanguage } = userPreferences;

  const { data: remote } = useSupabaseData({
    table: 'eng_communitylearning',
    mapRow: (row) => row,
    getFallback: () => null
  });

  const data = useMemo(() => {
    if (!remote || (Array.isArray(remote) && remote.length === 0)) return communityLearningData;
    const rows = Array.isArray(remote) ? remote : [remote];
    const out = { overview: communityLearningData.overview };
    // Stats
    out.statistics = { stats: [] };
    rows.filter(r => (r.type || r.section) === 'stat').forEach(r => {
      out.statistics.stats.push({
        id: r.stat_id || r.id,
        icon: r.icon || '👥',
        color: r.color || 'blue',
        value: r.value || 0,
        label: { tc: r.label_tc || '', sc: r.label_sc || '', en: r.label_en || '' }
      });
    });
    // Main actions
    out.mainActions = { actions: [] };
    rows.filter(r => (r.type || r.section) === 'action').forEach(r => {
      out.mainActions.actions.push({
        id: r.action_id || r.id,
        icon: r.icon || '👥',
        gradient: r.gradient || 'from-purple-500 to-pink-500',
        title: { tc: r.title_tc || '', sc: r.title_sc || '', en: r.title_en || '' }
      });
    });
    // Feed
    const feed = rows.filter(r => (r.type || r.section) === 'post').map(r => ({
      id: r.post_id || r.id,
      userAvatar: r.user_avatar || '👤',
      userName: { tc: r.user_name_tc || '', sc: r.user_name_sc || '', en: r.user_name_en || '' },
      timestamp: { tc: r.timestamp_tc || '', sc: r.timestamp_sc || '', en: r.timestamp_en || '' },
      content: { tc: r.content_tc || '', sc: r.content_sc || '', en: r.content_en || '' },
      likes: r.likes || 0,
      comments: r.comments || 0,
      shares: r.shares || 0,
      isLiked: false,
      isShared: false
    }));
    out.communityFeed = { title: communityLearningData.communityFeed.title, posts: feed.length > 0 ? feed : communityLearningData.communityFeed.posts };
    // Language exchange card
    const exch = rows.find(r => (r.type || r.section) === 'exchange');
    out.languageExchange = exch ? {
      title: communityLearningData.languageExchange.title,
      card: {
        gradient: exch.gradient || 'from-blue-500 to-cyan-500',
        icon: exch.icon || '🌐',
        title: { tc: exch.title_tc || '', sc: exch.title_sc || '', en: exch.title_en || '' },
        description: { tc: exch.description_tc || '', sc: exch.description_sc || '', en: exch.description_en || '' },
        buttonText: { tc: exch.button_tc || '開始配對', sc: exch.button_sc || '开始配对', en: exch.button_en || 'Start Matching' }
      }
    } : communityLearningData.languageExchange;
    // Navigation texts
    out.navigation = communityLearningData.navigation;
    return out;
  }, [remote]);

  const getText = (textObj) => {
    return textObj[displayLanguage] || textObj.en;
  };

  const handleFindPartner = () => {
    console.log('Find learning partner clicked');
    // Handle find partner action
  };

  const handleJoinGroupChat = () => {
    console.log('Join group chat clicked');
    // Handle join group chat action
  };

  const handleStartMatching = () => {
    console.log('Start language matching clicked');
    // Handle language exchange matching
  };

  const handleLikePost = (postId) => {
    setPosts(prevPosts => 
      prevPosts.map(post => 
        post.id === postId 
          ? { ...post, isLiked: !post.isLiked, likes: post.isLiked ? post.likes - 1 : post.likes + 1 }
          : post
      )
    );
  };

  const handleCommentPost = (postId) => {
    setSelectedPost(posts.find(post => post.id === postId));
    setShowCommentModal(true);
  };

  const handleSharePost = (postId) => {
    setPosts(prevPosts => 
      prevPosts.map(post => 
        post.id === postId 
          ? { ...post, isShared: !post.isShared, shares: post.isShared ? post.shares - 1 : post.shares + 1 }
          : post
      )
    );
  };

  const handleSubmitComment = () => {
    if (commentText.trim() && selectedPost) {
      setPosts(prevPosts => 
        prevPosts.map(post => 
          post.id === selectedPost.id 
            ? { ...post, comments: post.comments + 1 }
            : post
        )
      );
      setCommentText('');
      setShowCommentModal(false);
      setSelectedPost(null);
    }
  };

  const getColorClass = (color) => {
    switch (color) {
      case 'blue': return 'text-blue-400';
      case 'green': return 'text-green-400';
      case 'purple': return 'text-purple-400';
      default: return 'text-white';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white">
      {/* Header */}
      <div className="px-6 pt-12 pb-4">
        <div className="flex items-center justify-between mb-6">
          <button onClick={onBack} className="text-white">
            <ArrowLeft size={24} />
          </button>
          <h1 className="text-xl font-bold">
            {getText(data.overview.title)}
          </h1>
          <div className="w-6"></div> {/* Spacer for centering */}
        </div>
      </div>

      {/* Community Statistics */}
      <div className="px-6 mb-6">
        <div className="grid grid-cols-3 gap-4">
          {data.statistics.stats.map((stat) => (
            <div key={stat.id} className="bg-gray-800/30 border border-gray-600/30 rounded-xl p-4 text-center">
              <div className={`text-2xl mb-2 ${getColorClass(stat.color)}`}>
                {stat.icon}
              </div>
              <div className="text-2xl font-bold text-white mb-1">
                {stat.value}
              </div>
              <div className="text-sm text-gray-300">
                {getText(stat.label)}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Main Action Buttons */}
      <div className="px-6 mb-6">
        <div className="grid grid-cols-2 gap-4">
          {data.mainActions.actions.map((action) => (
            <button
              key={action.id}
              onClick={action.id === 'find_learning_partner' ? handleFindPartner : handleJoinGroupChat}
              className={`bg-gradient-to-r ${action.gradient} rounded-xl p-4 text-center transition-transform hover:scale-105`}
            >
              <div className="text-3xl mb-2">
                {action.icon}
              </div>
              <div className="font-semibold text-sm">
                {getText(action.title)}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Community Feed */}
      <div className="px-6 mb-6">
        <h2 className="text-lg font-semibold mb-4">
          {getText(data.communityFeed.title)}
        </h2>
        
        <div className="space-y-4">
          {posts.map((post) => (
            <div key={post.id} className="bg-gray-800/30 border border-gray-600/30 rounded-xl p-4">
              {/* Post Header */}
              <div className="flex items-center space-x-3 mb-3">
                <div className="text-2xl">
                  {post.userAvatar}
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-sm">
                    {getText(post.userName)}
                  </div>
                  <div className="text-xs text-gray-400">
                    {getText(post.timestamp)}
                  </div>
                </div>
              </div>

              {/* Post Content */}
              <div className="text-sm mb-4">
                {getText(post.content)}
              </div>

              {/* Post Interactions */}
              <div className="flex items-center space-x-6">
                <button
                  onClick={() => handleLikePost(post.id)}
                  className={`flex items-center space-x-1 text-sm transition-colors ${
                    post.isLiked ? 'text-red-400' : 'text-gray-400 hover:text-red-400'
                  }`}
                >
                  <span>❤️</span>
                  <span>{post.likes}</span>
                </button>
                
                <button
                  onClick={() => handleCommentPost(post.id)}
                  className="flex items-center space-x-1 text-sm text-gray-400 hover:text-blue-400 transition-colors"
                >
                  <span>💬</span>
                  <span>{post.comments}</span>
                </button>
                
                <button
                  onClick={() => handleSharePost(post.id)}
                  className={`flex items-center space-x-1 text-sm transition-colors ${
                    post.isShared ? 'text-green-400' : 'text-gray-400 hover:text-green-400'
                  }`}
                >
                  <span>📤</span>
                  <span>{post.shares}</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Language Exchange Section */}
      <div className="px-6 mb-8">
        <h2 className="text-lg font-semibold mb-4">
          {getText(data.languageExchange.title)}
        </h2>
        
        <div className={`bg-gradient-to-r ${data.languageExchange.card.gradient} rounded-xl p-6 relative overflow-hidden`}>
          {/* Background Icon */}
          <div className="absolute top-4 right-4 text-4xl opacity-20">
            {data.languageExchange.card.icon}
          </div>
          
          {/* Content */}
          <div className="relative z-10">
            <h3 className="text-lg font-bold mb-2">
              {getText(data.languageExchange.card.title)}
            </h3>
            <p className="text-sm mb-4 opacity-90">
              {getText(data.languageExchange.card.description)}
            </p>
            <button
              onClick={handleStartMatching}
              className="bg-white/20 hover:bg-white/30 text-white px-6 py-2 rounded-lg font-medium transition-colors"
            >
              {getText(data.languageExchange.card.buttonText)}
            </button>
          </div>
        </div>
      </div>

      {/* Bottom Navigation */}
      <BottomNavigation
        currentView="community_learning"
        onHomeClick={onBack}
        onLearningClick={onBack}
        onChallengeClick={onBack}
        onSocialClick={onBack}
      />

      {/* Comment Modal */}
      {showCommentModal && selectedPost && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gray-800 rounded-xl p-6 w-full max-w-sm">
            <h3 className="text-lg font-bold mb-4">
              {getText(data.navigation.commentButton)}
            </h3>
            
            <textarea
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder={getText(data.navigation.commentButton)}
              className="w-full p-3 bg-gray-700 rounded-lg text-white placeholder-gray-400 resize-none"
              rows="3"
            />
            
            <div className="flex space-x-3 mt-4">
              <button
                onClick={() => {
                  setShowCommentModal(false);
                  setSelectedPost(null);
                  setCommentText('');
                }}
                className="flex-1 py-2 px-4 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitComment}
                className="flex-1 py-2 px-4 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommunityLearningPage;
